import type { Driver } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { Vehicle, VehicleGroup } from '@cobliteam/common-types';
import type { PlainGeofence } from '@cobliteam/common-types/src/geofenceV3';
import type { MentionableEntity, MentionableEntityType, SearchDataset, SearchGeofence, SearchOptions, SearchVehicle, SearchVehicleGroup } from './types';

export const EMPTY_SEARCH_DATASET: SearchDataset = {
  vehicles: [],
  drivers: [],
  devices: [],
  vehicleGroups: [],
  geofences: [],
};

export const DEFAULT_MENTIONABLE_ENTITY_TYPES: MentionableEntityType[] = ['vehicles', 'drivers', 'devices'];

export const normalizeSearchOptions = (options?: SearchOptions): Required<SearchOptions> => {
  const vehicleGroupIds = [...new Set(options?.vehicleGroupIds ?? [])].sort((first, second) => first.localeCompare(second));

  return {
    vehicleGroupIds,
    includeObservationPeriodTrigger: options?.includeObservationPeriodTrigger ?? false,
    includeGeofences: options?.includeGeofences ?? true,
  };
};

export const getSearchDatasetKey = (options?: SearchOptions) => {
  const normalizedOptions = normalizeSearchOptions(options);
  const serializedGroupIds = normalizedOptions.vehicleGroupIds.join(',');

  return JSON.stringify({
    vehicleGroupIds: serializedGroupIds,
    includeObservationPeriodTrigger: normalizedOptions.includeObservationPeriodTrigger,
    includeGeofences: normalizedOptions.includeGeofences,
  });
};

const buildVehicles = (vehicles?: Vehicle[]): SearchVehicle[] =>
  vehicles?.map((vehicle) => ({
    ...vehicle,
    kind: 'vehicle',
    tagsIndex: vehicle.tags?.map((tag) => tag.name).join(', ') ?? '',
  })) ?? [];

const buildVehicleGroups = (vehicleGroups: VehicleGroup[] | undefined, vehicles: SearchVehicle[]): SearchVehicleGroup[] =>
  vehicleGroups?.map((vehicleGroup) => ({
    ...vehicleGroup,
    kind: 'vehicleGroup',
    vehicles: vehicles.filter((vehicle) => vehicleGroup.vehicleIds?.includes(vehicle.id)),
  })) ?? [];

const buildGeofences = (geofences?: PlainGeofence[]): SearchGeofence[] =>
  geofences?.map((geofence) => ({
    ...geofence,
    kind: 'geofence',
  })) ?? [];

export const buildSearchDataset = ({
  vehicles,
  drivers,
  devices,
  vehicleGroups,
  geofences,
}: {
  vehicles?: Vehicle[];
  drivers?: Driver[];
  devices?: DeviceFragment[];
  vehicleGroups?: VehicleGroup[];
  geofences?: PlainGeofence[];
}): SearchDataset => {
  const normalizedVehicles = buildVehicles(vehicles);

  return {
    vehicles: normalizedVehicles,
    drivers:
      drivers?.map((driver) => ({
        ...driver,
        kind: 'driver',
      })) ?? [],
    devices:
      devices?.map((device) => ({
        ...device,
        kind: 'device',
      })) ?? [],
    vehicleGroups: buildVehicleGroups(vehicleGroups, normalizedVehicles),
    geofences: buildGeofences(geofences),
  };
};

export const getMentionableEntities = (dataset: SearchDataset, entityTypes: MentionableEntityType[] = DEFAULT_MENTIONABLE_ENTITY_TYPES): MentionableEntity[] => {
  const uniqueEntityTypes = [...new Set(entityTypes)];

  return uniqueEntityTypes.reduce<MentionableEntity[]>((entities, entityType) => {
    switch (entityType) {
      case 'vehicles':
        entities.push(...dataset.vehicles);
        break;
      case 'drivers':
        entities.push(...dataset.drivers);
        break;
      case 'devices':
        entities.push(...dataset.devices);
        break;
      case 'vehicleGroups':
        entities.push(...dataset.vehicleGroups);
        break;
      case 'geofences':
        entities.push(...dataset.geofences);
        break;
      default:
        break;
    }

    return entities;
  }, []);
};
